/**
 * NextDayPreviewCard — Day-1 Retention Quick Win #3
 *
 * 「明日預告」card surfaced at session end (or next-morning first launch). Shows
 * the kid 3 concrete reasons to come back tomorrow:
 *
 *   Line 1  火焰連續 streak preview     — "明天你的火焰連續會 +1 變成 🔥 N+1"
 *   Line 2  Day-7 限定塗裝倒數         — "再 X 天解鎖限定塗裝" / 解鎖後升級訊息
 *   Line 3  下一關預告                 — "新關卡:[科目] 第 N 課" (from campaignState)
 *
 * Sits on the **home (retention)** layer per CLAUDE.md 紅線 — 答題畫面禁彩光 /
 * mascot,but home banner 允許「明天等你」這類 retention 文案 + 配色。
 *
 * Reuses the home-banner-queue skeleton (IDEA-015 / src/learn/home-banner-queue.ts);
 * the trigger gate (`nextDayPreview:lastShownISO`) is a separate util so unit tests
 * can exercise it without rendering DOM.
 *
 * See docs/research/day1-retention-k3-onboarding.md QW#3 for source rationale.
 */

import type { PlayerProgress, Subject } from '../../shared/types';

/** localStorage key — UTC date (YYYY-MM-DD) of last surface. */
export const NEXT_DAY_PREVIEW_GATE_KEY = 'nextDayPreview:lastShownISO';
/** localStorage flag set by session-end (paper-view) to request next surface. */
export const NEXT_DAY_PREVIEW_PENDING_KEY = 'nextDayPreview:pending';

/** Mark a session as just ended — call from quiz/exam side-effects. */
export function markSessionEnded(): void {
  try { localStorage.setItem(NEXT_DAY_PREVIEW_PENDING_KEY, '1'); } catch { /* noop */ }
}

/** Clear the pending flag — call after the banner is surfaced. */
export function clearSessionPending(): void {
  try { localStorage.removeItem(NEXT_DAY_PREVIEW_PENDING_KEY); } catch { /* noop */ }
}

/** True iff a session just ended and gate allows showing. */
export function isNextDayPreviewPending(nowMs: number = Date.now()): boolean {
  try {
    const pending = localStorage.getItem(NEXT_DAY_PREVIEW_PENDING_KEY);
    if (pending !== '1') return false;
    return shouldShowNextDayPreview(nowMs);
  } catch {
    return false;
  }
}

/** Day-7 milestone — limited paint reward unlock day. */
const COSMETIC_UNLOCK_DAY = 7;

/** Subject → 顯示名 (no faction names per CLAUDE.md 紅線 — but home 層 OK). */
const SUBJECT_LABELS: Record<Subject, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

/** UTC YYYY-MM-DD — shared with streak module conventions. */
function utcDateString(nowMs: number = Date.now()): string {
  return new Date(nowMs).toISOString().slice(0, 10);
}

/**
 * Returns true iff the preview should surface now (i.e. NOT already shown today).
 * Pure read — call before render to decide whether to mount the banner slot.
 *
 * Why a separate util: home.ts may want to decide upfront whether to even reserve
 * a slot in the priority queue. Also enables unit-testing the gate without DOM.
 */
export function shouldShowNextDayPreview(nowMs: number = Date.now()): boolean {
  try {
    const last = localStorage.getItem(NEXT_DAY_PREVIEW_GATE_KEY);
    if (!last) return true;
    return last !== utcDateString(nowMs);
  } catch {
    // localStorage unavailable (private mode / SSR) → don't spam, but allow once.
    return true;
  }
}

/** Mark the preview as shown today — call from the renderer once mounted. */
export function markNextDayPreviewShown(nowMs: number = Date.now()): void {
  try {
    localStorage.setItem(NEXT_DAY_PREVIEW_GATE_KEY, utcDateString(nowMs));
  } catch {
    /* noop — best-effort persistence */
  }
}

/** Reset the gate (test helper). Not exported in production wiring. */
export function _resetNextDayPreviewGate(): void {
  try {
    localStorage.removeItem(NEXT_DAY_PREVIEW_GATE_KEY);
  } catch {
    /* noop */
  }
}

/** Internal: pick the next chapter & subject to tease in Line 3. */
function pickNextChapterTease(p: PlayerProgress | null | undefined): {
  subject: Subject | null;
  chapterNumber: number;
} | null {
  if (!p) return null;
  const cs = p.campaignState;
  if (!cs) return null;
  const next = (cs.chapter ?? 1);
  // Slay-Spire chapter is 1-5 (per types.ts line 73).
  if (next < 1 || next > 5) return null;
  // Without an explicit per-subject unlocked-chapter map (not on PlayerProgress),
  // fall back to the math/chinese MVP focus per CLAUDE.md 三年級 Phase 1.
  // We pick math when chapter is odd, chinese when even — both are Phase 1 MVP
  // subjects so the kid actually has content to land on.
  const subject: Subject = next % 2 === 1 ? 'math' : 'chinese';
  return { subject, chapterNumber: next };
}

/** Internal: tomorrow's streak count given today's progress. */
function tomorrowStreakDays(p: PlayerProgress | null | undefined): number {
  if (!p) return 1;
  const cur = p.streak?.days ?? 0;
  // If kid logged in today, tomorrow becomes cur + 1; if not, tomorrow becomes 1
  // (today gets claimed first). Either way the *tease* should land on a +1 framing.
  return Math.max(1, cur + 1);
}

/**
 * Render the 明日預告 card into `container`. Replaces any existing child rather
 * than appending (banner-slot pattern — same slot can re-render).
 *
 * Marks the gate (`nextDayPreview:lastShownISO`) on mount so the kid doesn't
 * see this twice in one calendar day.
 */
export function renderNextDayPreviewCard(
  container: HTMLElement,
  progress: PlayerProgress | null | undefined,
  onDismiss: () => void,
  nowMs: number = Date.now(),
): void {
  // Replace prior child(ren) — banner slots re-render in place.
  container.innerHTML = '';

  const card = document.createElement('div');
  card.className = 'next-day-preview-card retention-banner';
  card.setAttribute('role', 'region');
  card.setAttribute('aria-label', '明日預告');
  card.style.cssText = [
    'position: relative',
    'background: linear-gradient(135deg, #1a2b4d 0%, #2a3f7a 100%)',
    'color: #fff',
    'border-radius: 14px',
    'padding: 18px 20px',
    'margin: 12px 0',
    'box-shadow: 0 4px 18px rgba(42, 63, 122, 0.35)',
    'overflow: hidden',
  ].join(';');

  // ── Line 1: streak preview ─────────────────────────────────────────────────
  const tomorrowStreak = tomorrowStreakDays(progress);
  const line1 = `明天你的火焰連續會 +1 變成 🔥 ${tomorrowStreak}`;

  // ── Line 2: Day-7 cosmetic countdown ───────────────────────────────────────
  const curStreak = progress?.streak?.days ?? 0;
  let line2: string;
  if (curStreak >= COSMETIC_UNLOCK_DAY) {
    line2 = '🎨 限定塗裝已解鎖,繼續登入升級配色';
  } else {
    const daysLeft = Math.max(1, COSMETIC_UNLOCK_DAY - curStreak);
    line2 = `再 ${daysLeft} 天解鎖限定塗裝`;
  }

  // ── Line 3: next chapter tease ─────────────────────────────────────────────
  const tease = pickNextChapterTease(progress);
  let line3: string;
  if (!tease || !tease.subject) {
    line3 = '🎓 全章節已解鎖,複習挑戰!';
  } else {
    line3 = `新關卡:${SUBJECT_LABELS[tease.subject]} 第 ${tease.chapterNumber} 課`;
  }

  card.innerHTML = `
    <div class="ndp-header" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;">
      <strong style="font-size:16px;letter-spacing:1px;">🌅 明日預告</strong>
      <button class="ndp-dismiss" aria-label="關閉預告"
        style="background:transparent;border:1px solid rgba(255,255,255,0.4);color:#fff;border-radius:8px;padding:4px 10px;font-size:12px;cursor:pointer;">
        知道了
      </button>
    </div>
    <ul class="ndp-lines" style="list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:6px;font-size:14px;line-height:1.5;">
      <li class="ndp-line-1">${escapeHtml(line1)}</li>
      <li class="ndp-line-2">${escapeHtml(line2)}</li>
      <li class="ndp-line-3">${escapeHtml(line3)}</li>
    </ul>
  `;

  const dismissBtn = card.querySelector<HTMLButtonElement>('.ndp-dismiss');
  dismissBtn?.addEventListener('click', () => {
    card.remove();
    try { onDismiss(); } catch (err) { console.warn('[ndp] onDismiss threw:', err); }
  });

  container.appendChild(card);
  markNextDayPreviewShown(nowMs);
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
